import {
  LoadableState,
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { SavedSearch } from '~/contracts/srp';
import type { DeepPartial } from '~/types';

export type SavedSearchesContextType = {
  fetchSavedSearchTrigger: number;
  savedSearches: LoadableState<SavedSearch[]>;
  triggerFetchSavedSearches: () => void;
};

const defaultContext: SavedSearchesContextType = {
  fetchSavedSearchTrigger: 0,
  savedSearches: defaultLoadableState<SavedSearch[]>([]),
  triggerFetchSavedSearches() {}
};

const SavedSearchesContext =
  createContext<SavedSearchesContextType>(defaultContext);

export function SavedSearchesContextProvider({ children }) {
  const [fetchSavedSearchTrigger, setTrigger] = useState<number>(
    defaultContext.fetchSavedSearchTrigger
  );
  const savedSearches = useLoadableState<SavedSearch[]>(
    defaultContext.savedSearches.data
  );

  const triggerFetchSavedSearches = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<SavedSearchesContextType>(
    () => ({
      fetchSavedSearchTrigger,
      savedSearches,
      triggerFetchSavedSearches
    }),
    [fetchSavedSearchTrigger, savedSearches, triggerFetchSavedSearches]
  );

  return (
    <SavedSearchesContext.Provider value={value}>
      {children}
    </SavedSearchesContext.Provider>
  );
}

SavedSearchesContextProvider.displayName = 'SavedSearchesContextProvider';

export const generateSavedSearchesContext = (
  customState: DeepPartial<SavedSearchesContextType>
): SavedSearchesContextType =>
  deepmerge<SavedSearchesContextType, DeepPartial<SavedSearchesContextType>>(
    defaultContext,
    customState
  );

export function useSavedSearches(): SavedSearch[] {
  const { savedSearches } = useContext(SavedSearchesContext);

  return savedSearches.data;
}

export function useSavedSearchesIsLoaded(): boolean {
  const { savedSearches } = useContext(SavedSearchesContext);

  return savedSearches.isLoaded;
}

export default SavedSearchesContext;
